import { Link } from "react-router-dom";
import type { Character } from "@/types";
import Badge from "@/components/base/Badge";
import CharacterCard from "@/components/base/CharacterCard";
import DetailActions from "./DetailActions";

interface CharacterDetailProps {
  character: Character;
  related: Character[];
}

export default function CharacterDetail({ character, related }: CharacterDetailProps) {
  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <div className="mx-auto w-full max-w-[320px] lg:mx-0">
          <div className="aspect-square w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={character.avatar}
              alt={`角色 ${character.name} 立绘`}
              title={`${character.name} 角色介绍详情`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="accent">{character.roleType}</Badge>
            {character.tags.map((tag) => (
              <Badge key={tag} tone="secondary">
                {tag}
              </Badge>
            ))}
          </div>
          <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">
            {character.name}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            出自作品：
            <Link
              to={`/detail/${character.animeId}`}
              className="cursor-pointer font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              《{character.animeTitle}》
            </Link>
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{character.description}</p>
          <div className="mt-6">
            <DetailActions id={character.id} title={character.name} />
          </div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            人物背景
          </h2>
          <p className="text-sm leading-7 text-foreground-700">{character.background}</p>

          <h2 className="mb-4 mt-8 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            作品关系
          </h2>
          <ul className="overflow-hidden rounded-lg border border-background-200">
            {character.relations.map((relation, index) => (
              <li
                key={relation.name}
                className={`flex items-center justify-between gap-4 p-3.5 ${
                  index % 2 === 0 ? "bg-background-50" : "bg-background-100"
                }`}
              >
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-user-3-line text-secondary-600"></i>
                  {relation.name}
                </span>
                <span className="text-right text-xs text-foreground-500">{relation.relation}</span>
              </li>
            ))}
          </ul>

          <h2 className="mb-3 mt-8 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            代表台词
          </h2>
          <ul className="space-y-3">
            {character.quotes.map((quote) => (
              <li
                key={quote}
                className="rounded-lg border-l-4 border-accent-400 bg-accent-50 p-4 text-sm italic leading-relaxed text-foreground-700"
              >
                {quote}
              </li>
            ))}
          </ul>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-3 text-base font-bold text-foreground-950">分类标签</h2>
            <div className="flex flex-wrap gap-2">
              <Badge tone="accent">{character.roleType}</Badge>
              {character.tags.map((tag) => (
                <Badge key={tag} tone="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
            <Link
              to="/category/characters"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              查看全部角色介绍
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-3 text-base font-bold text-foreground-950">数据速览</h2>
            <ul className="space-y-2.5 text-sm text-foreground-600">
              <li className="flex items-center justify-between">
                <span>角色属性</span>
                <span className="font-medium text-foreground-800">{character.roleType}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>收录时间</span>
                <span className="font-medium text-foreground-800">{character.publishTime}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>人气热度</span>
                <span className="font-medium text-foreground-800">{character.views}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 text-lg font-bold text-foreground-950">相关角色推荐</h2>
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {related.map((item) => (
              <CharacterCard key={item.id} character={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}